// Ponte com o desktop (Tauri 2). Na web (navegador) não existe janela flutuante do
// sistema: getDesktop() devolve null e o app segue com o timer no título da aba (P0.10).
import type { PomodoroState } from '../domain/pomodoro';
import type { Task } from '../domain/task';
import type { FinishResult } from '../domain/timer';
import {
  FLOAT_EXPANDED,
  FLOAT_SIZE,
  loadSavedPosition,
  placeFloat,
  resizeInPlace,
  savePosition,
  type Monitor,
} from '../domain/floatPosition';

/** O que a janela principal manda para a barra flutuante. */
export interface FloatSnapshot {
  task: Task | null;
  offline: boolean;
  pomodoro: PomodoroState | null;
  /** comemoração na barra: meme + confetes + resultado (AC-046); quiet = só "Feito!" verde de 0,5 s (AC-230) */
  celebrating: { title: string; result: FinishResult; meme: string; quiet?: boolean } | null;
  clockOffset: number;
  language: string;
  theme: 'light' | 'dark';
}

export type FloatCommand =
  | { type: 'pause'; id: string }
  | { type: 'resume'; id: string }
  | { type: 'finish'; id: string }
  | { type: 'start'; id: string }
  | { type: 'skipBreak' }
  | { type: 'relax' }
  | { type: 'open' }
  | { type: 'ready' };

type Unlisten = () => void;

export interface DesktopBridge {
  /** label da janela atual: "main" ou "float" */
  label: string;
  showFloat(): Promise<void>;
  hideFloat(): Promise<void>;
  /** cresce para a comemoração (300 × 260) ou volta ao tamanho de barra (300 × 48) */
  setFloatExpanded(expanded: boolean): Promise<void>;
  minimizeMain(): Promise<void>;
  restoreMain(): Promise<void>;
  sendToFloat(snapshot: FloatSnapshot): Promise<void>;
  onFloatCommand(cb: (cmd: FloatCommand) => void): Promise<Unlisten>;
  onSnapshot(cb: (snapshot: FloatSnapshot) => void): Promise<Unlisten>;
  sendCommand(cmd: FloatCommand): Promise<void>;
  /** guarda a posição da barra quando o usuário arrasta */
  trackFloatPosition(): Promise<Unlisten>;
  startDragging(): Promise<void>;
  openExternal(url: string): Promise<void>;
  onDeepLink(cb: (url: string) => void): Promise<Unlisten>;
  invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T>;
  notify(title: string, body: string): Promise<void>;
}

export function isTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

const SNAPSHOT_EVENT = 'beedoit://float-snapshot';
const COMMAND_EVENT = 'beedoit://float-command';

let cached: Promise<DesktopBridge | null> | null = null;

export function getDesktop(): Promise<DesktopBridge | null> {
  if (!isTauri()) return Promise.resolve(null);
  cached ??= createTauriBridge();
  return cached;
}

async function createTauriBridge(): Promise<DesktopBridge> {
  const win = await import('@tauri-apps/api/window');
  const dpi = await import('@tauri-apps/api/dpi');
  const ev = await import('@tauri-apps/api/event');
  const core = await import('@tauri-apps/api/core');
  const current = win.getCurrentWindow();
  const byLabel = async (label: string) => {
    const w = await win.Window.getByLabel(label);
    if (!w) throw new Error(`janela ${label} não existe`);
    return w;
  };

  const toMonitor = (m: { name: string | null; position: { x: number; y: number }; size: { width: number; height: number }; scaleFactor: number }): Monitor => ({
    name: m.name,
    x: m.position.x,
    y: m.position.y,
    width: m.size.width,
    height: m.size.height,
    scaleFactor: m.scaleFactor,
  });

  let restoreTo: { x: number; y: number } | null = null;

  return {
    label: current.label,
    async showFloat() {
      const float = await byLabel('float');
      const monitors = (await win.availableMonitors()).map(toMonitor);
      const primary = await win.primaryMonitor();
      if (monitors.length) {
        const place = placeFloat(loadSavedPosition(localStorage), monitors, primary ? toMonitor(primary) : undefined);
        await float.setSize(new dpi.LogicalSize(FLOAT_SIZE.width, FLOAT_SIZE.height));
        await float.setPosition(new dpi.PhysicalPosition(place.x, place.y));
      }
      await float.setAlwaysOnTop(true);
      await float.show();
    },
    async hideFloat() {
      await (await byLabel('float')).hide();
    },
    async setFloatExpanded(expanded) {
      const float = await byLabel('float');
      const size = expanded ? FLOAT_EXPANDED : FLOAT_SIZE;
      if (expanded) {
        // guarda onde a barra estava: se crescer a empurrar para cima, ela volta depois
        const pos = await float.outerPosition();
        restoreTo = { x: pos.x, y: pos.y };
        const monitors = (await win.availableMonitors()).map(toMonitor);
        if (monitors.length) {
          const place = resizeInPlace(restoreTo, size, monitors);
          await float.setPosition(new dpi.PhysicalPosition(place.x, place.y));
        }
      } else if (restoreTo) {
        await float.setPosition(new dpi.PhysicalPosition(restoreTo.x, restoreTo.y));
        restoreTo = null;
      }
      await float.setSize(new dpi.LogicalSize(size.width, size.height));
    },
    async minimizeMain() {
      await (await byLabel('main')).minimize();
    },
    async restoreMain() {
      const main = await byLabel('main');
      await main.unminimize();
      await main.show();
      await main.setFocus();
    },
    async sendToFloat(snapshot) {
      await ev.emitTo('float', SNAPSHOT_EVENT, snapshot);
    },
    async onFloatCommand(cb) {
      return ev.listen<FloatCommand>(COMMAND_EVENT, (e) => cb(e.payload));
    },
    async onSnapshot(cb) {
      return current.listen<FloatSnapshot>(SNAPSHOT_EVENT, (e) => cb(e.payload));
    },
    async sendCommand(cmd) {
      await ev.emitTo('main', COMMAND_EVENT, cmd);
    },
    async trackFloatPosition() {
      return current.onMoved(({ payload }) => savePosition(localStorage, { x: payload.x, y: payload.y }));
    },
    async startDragging() {
      await current.startDragging();
    },
    async openExternal(url) {
      const { openUrl } = await import('@tauri-apps/plugin-opener');
      await openUrl(url);
    },
    async onDeepLink(cb) {
      const dl = await import('@tauri-apps/plugin-deep-link');
      const start = await dl.getCurrent();
      start?.forEach(cb);
      return dl.onOpenUrl((urls) => urls.forEach(cb));
    },
    invoke: (cmd, args) => core.invoke(cmd, args),
    async notify(title, body) {
      const n = await import('@tauri-apps/plugin-notification');
      let granted = await n.isPermissionGranted();
      if (!granted) granted = (await n.requestPermission()) === 'granted';
      if (granted) n.sendNotification({ title, body });
    },
  };
}
